// Shipping address rules per country. Printify validates again server-side; this is so customers
// see what's wrong before a request is made, and so regions are sent as the codes Printify expects.

export type ShippingField = 'firstName' | 'lastName' | 'email' | 'line1' | 'line2' | 'city' | 'region' | 'postalCode' | 'country';
export type ShippingAddress = Record<ShippingField, string>;
export type ShippingErrors = Partial<Record<ShippingField, string>>;

interface CountryRules {
  code: string;
  name: string;
  regionLabel: string;
  regions: {code: string, name: string}[];
  postalLabel: string;
  postalName: string; // the label as it reads mid-sentence
  postalPlaceholder: string;
  postalPattern: RegExp;
  // How the postal code is sent once valid, e.g. "h2x1y4" -> "H2X 1Y4"
  formatPostal: (value: string) => string;
}

const CA_PROVINCES = [
  ['AB', 'Alberta'], ['BC', 'British Columbia'], ['MB', 'Manitoba'], ['NB', 'New Brunswick'],
  ['NL', 'Newfoundland and Labrador'], ['NS', 'Nova Scotia'], ['NT', 'Northwest Territories'], ['NU', 'Nunavut'],
  ['ON', 'Ontario'], ['PE', 'Prince Edward Island'], ['QC', 'Quebec'], ['SK', 'Saskatchewan'], ['YT', 'Yukon'],
];

const US_STATES = [
  ['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'], ['CA', 'California'], ['CO', 'Colorado'],
  ['CT', 'Connecticut'], ['DE', 'Delaware'], ['DC', 'District of Columbia'], ['FL', 'Florida'], ['GA', 'Georgia'],
  ['HI', 'Hawaii'], ['ID', 'Idaho'], ['IL', 'Illinois'], ['IN', 'Indiana'], ['IA', 'Iowa'], ['KS', 'Kansas'],
  ['KY', 'Kentucky'], ['LA', 'Louisiana'], ['ME', 'Maine'], ['MD', 'Maryland'], ['MA', 'Massachusetts'],
  ['MI', 'Michigan'], ['MN', 'Minnesota'], ['MS', 'Mississippi'], ['MO', 'Missouri'], ['MT', 'Montana'],
  ['NE', 'Nebraska'], ['NV', 'Nevada'], ['NH', 'New Hampshire'], ['NJ', 'New Jersey'], ['NM', 'New Mexico'],
  ['NY', 'New York'], ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'], ['OK', 'Oklahoma'],
  ['OR', 'Oregon'], ['PA', 'Pennsylvania'], ['RI', 'Rhode Island'], ['SC', 'South Carolina'], ['SD', 'South Dakota'],
  ['TN', 'Tennessee'], ['TX', 'Texas'], ['UT', 'Utah'], ['VT', 'Vermont'], ['VA', 'Virginia'], ['WA', 'Washington'],
  ['WV', 'West Virginia'], ['WI', 'Wisconsin'], ['WY', 'Wyoming'],
];

const toRegions = (list: string[][]) => list.map(([code, name]) => ({code, name}));

// Where most customers are; Printify ships further, so more countries can be added here
export const COUNTRIES: CountryRules[] = [
  {
    code: 'CA', name: 'Canada',
    regionLabel: 'Province', regions: toRegions(CA_PROVINCES),
    postalLabel: 'Postal code', postalName: 'postal code', postalPlaceholder: 'H2X 1Y4',
    // Canadian postal codes never use D, F, I, O, Q or U, nor W or Z as the first letter
    postalPattern: /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z] ?\d[ABCEGHJ-NPRSTV-Z]\d$/,
    formatPostal: (value) => {
      const compact = value.replace(/\s+/g, '').toUpperCase();
      return `${compact.slice(0, 3)} ${compact.slice(3)}`;
    },
  },
  {
    code: 'US', name: 'United States',
    regionLabel: 'State', regions: toRegions(US_STATES),
    postalLabel: 'ZIP code', postalName: 'ZIP code', postalPlaceholder: '10001',
    postalPattern: /^\d{5}(-\d{4})?$/,
    formatPostal: (value) => value.trim(),
  },
];

export const countryRules = (code: string) => COUNTRIES.find(country => country.code === code) ?? COUNTRIES[0];

export const EMPTY_ADDRESS: ShippingAddress = {
  firstName: '', lastName: '', email: '', line1: '', line2: '', city: '', region: '', postalCode: '', country: COUNTRIES[0].code,
};

export const REQUIRED_FIELDS: ShippingField[] = ['firstName', 'lastName', 'email', 'line1', 'city', 'region', 'postalCode', 'country'];

const MAX_LENGTH = 100;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Every problem with the address, keyed by field; empty when it can be sent. */
export const validateShipping = (address: ShippingAddress): ShippingErrors => {
  const errors: ShippingErrors = {};
  const rules = countryRules(address.country);

  REQUIRED_FIELDS.forEach(field => {
    if (!address[field].trim()) errors[field] = 'Required';
  });
  (Object.keys(address) as ShippingField[]).forEach(field => {
    if (!errors[field] && address[field].trim().length > MAX_LENGTH) errors[field] = `At most ${MAX_LENGTH} characters`;
  });

  if (!errors.email && !EMAIL_PATTERN.test(address.email.trim())) {
    errors.email = 'Enter a valid email, e.g. jane@example.com';
  }
  if (!errors.country && !COUNTRIES.some(country => country.code === address.country)) {
    errors.country = 'We don\'t ship to this country yet';
  }
  if (!errors.region && !rules.regions.some(region => region.code === address.region)) {
    errors.region = `Choose a ${rules.regionLabel.toLowerCase()}`;
  }
  if (!errors.postalCode && !rules.postalPattern.test(address.postalCode.replace(/\s+/g, ' ').trim().toUpperCase())) {
    errors.postalCode = `Enter a valid ${rules.postalName}, e.g. ${rules.postalPlaceholder}`;
  }
  return errors;
};

/** The address as it's sent: trimmed, with the postal code in its standard form. */
export const normalizeShipping = (address: ShippingAddress): ShippingAddress => {
  const trimmed = Object.fromEntries(Object.entries(address).map(([field, value]) => [field, value.trim()])) as ShippingAddress;
  return {...trimmed, postalCode: countryRules(address.country).formatPostal(trimmed.postalCode)};
};
